import React from 'react';
import { startOfDay } from 'date-fns';
import { format } from 'date-fns-tz';
import { merge } from 'lodash-es';
import { useMutation, UseMutationOptions } from 'react-query';
import { useDispatch } from 'react-redux';
import { Trans } from 'react-i18next';

import { ToastVariant, useDateFns, useProvider, useToastMessage } from '~/Hooks';
import { EVENT_SERVICE_URLS } from '~/Constants/Urls.constants';
import { EventType, IEvent } from '~/Models/DTO/Event.model';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { REQUEST_ACTIONS, STATUS } from '~/Constants/Actions.constants';
import { extractErrorMessage } from '~/Helpers/Error.helper';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { ICalendarEvent } from '~/Screens/Calendar/interfaces';
import { useDefaultTimeSeries } from '~/Screens/Events/EventCreate/EventCreateSections/EventScheduleSection/EventScheduleTimeSeries/useDefaultTimeSeries';
import { EventGroup } from '~/Models/DTO/EventGroup.model';
import { IEventSeries } from '~/Models/DTO/EventSeries.model';
import { createQueriedURL } from '~/Helpers/String.helper';

export const useCreateNewCalendarEvent = ({
  onSettled,
}: Omit<UseMutationOptions<void, IErrorResponse, ICalendarEvent>, 'mutationFn'>) => {
  const dispatch = useDispatch();
  const { showToast } = useToastMessage();
  const { currentProvider } = useProvider();
  const { getDefaultTimeSeries } = useDefaultTimeSeries();
  const { convertToProviderTimezone } = useDateFns();

  const eventAction = useMutation(
    async ({ start, end, ...event }: ICalendarEvent) => {
      if (!currentProvider) {
        return;
      }
      const { domain, uuid: providerId } = currentProvider;
      const createdEvent = await requestReactQuery<EventGroup>({
        url:
          event.type === EventType.FREE_TRAINING
            ? EVENT_SERVICE_URLS.FREE_TRAININGS
            : EVENT_SERVICE_URLS.EVENT_GROUPS,
        config: { method: 'POST' },
        data: {
          ...event,
          domain,
          providerId,
        },
      });
      const startDate = convertToProviderTimezone(start);
      const endDate = convertToProviderTimezone(end);
      const timeSeries = getDefaultTimeSeries({
        eventType: event.type,
        startDate: startOfDay(new Date(startDate)).toISOString(),
        eventGroupId: createdEvent.id,
      });
      await requestReactQuery<IEventSeries>({
        url: EVENT_SERVICE_URLS.EVENT_SERIES,
        config: { method: 'POST' },
        data: {
          ...merge(timeSeries, {
            dailyStartTime: format(new Date(startDate), 'HH:mm:ss'),
            dailyEndTime: format(new Date(endDate), 'HH:mm:ss'),
            startDate: format(new Date(timeSeries.startDate), 'yyyy-MM-dd'),
            endDate: format(new Date(timeSeries.endDate), 'yyyy-MM-dd'),
          }),
        },
      });
      const newEvent = await requestReactQuery<IEvent[]>({
        url: createQueriedURL(EVENT_SERVICE_URLS.EVENT_LIST, {
          providerId,
          eventGroupId: createdEvent.id,
        }),
        config: { method: 'GET' },
      });
      if (newEvent.length) {
        dispatch({
          type: REQUEST_ACTIONS.CREATE_EVENT,
          name: REQUEST_ACTIONS.CREATE_EVENT,
          status: STATUS.SUCCESS,
          payload: newEvent[0],
        });
      }
    },
    {
      onError: (errorObj: IErrorResponse) => {
        const message = extractErrorMessage(errorObj) as any;
        showToast(
          'Event creating error',
          <Trans i18nKey={message?.key} defaults={message?.defaultMessage || message} />,
          ToastVariant.ERROR,
        );
      },
      onSettled,
    },
  );

  return { eventAction };
};
